import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { SITE_URL } from '../../config';
import { db } from '../../firebase';

function isValidEmail(value) {
  const v = String(value || '').trim();
  if (!v) return false;
  // Lightweight validation; rely on HTML5 for stricter UX in-browser.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function TipsPage() {
  const location = useLocation();
  const [name, setName] = useState('');
  const [tipName, setTipName] = useState('');
  const [email, setEmail] = useState('');
  const [telegram, setTelegram] = useState('');
  const [tips, setTips] = useState('');

  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const canSubmit = useMemo(() => {
    return isValidEmail(email) && String(tips || '').trim().length > 0 && !loading;
  }, [email, tips, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');

    const cleaned = {
      name: String(name || '').trim() || '',
      tipName: String(tipName || '').trim() || '',
      email: String(email || '').trim(),
      telegram: String(telegram || '').trim() || '',
      tips: String(tips || '').trim(),
    };

    if (!isValidEmail(cleaned.email)) {
      setStatus('Please enter a valid email address.');
      return;
    }

    if (!cleaned.tips) {
      setStatus('Please enter your tips.');
      return;
    }

    setLoading(true);
    setStatus('Submitting…');

    try {
      await addDoc(collection(db, 'tips'), {
        ...cleaned,
        status: 'new',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setStatus('Thanks — your tip has been submitted.');
      setName('');
      setTipName('');
      setEmail('');
      setTelegram('');
      setTips('');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit tip', err);
      setStatus('Failed to submit tip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canonicalUrl = `${SITE_URL}${location.pathname}`;

  return (
    <section className="content-card">
      <Helmet>
        <title>Submit a tip – IGAMINGSPOT</title>
        <meta
          name="description"
          content="Submit a confidential tip to IGAMINGSPOT. Share operator updates, compliance issues, or market signals."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Submit a tip – IGAMINGSPOT" />
        <meta
          property="og:description"
          content="Submit a confidential tip to IGAMINGSPOT."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SITE_URL}/logo512.png`} />
        <meta property="og:site_name" content="IGAMINGSPOT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Submit a tip – IGAMINGSPOT" />
        <meta name="twitter:description" content="Submit a confidential tip to IGAMINGSPOT." />
        <meta name="twitter:image" content={`${SITE_URL}/logo512.png`} />
      </Helmet>
      <div className="content-card-header">
        <div>
          <div className="section-title">Tips</div>
          <h1 className="section-subtitle">Submit a tip</h1>
        </div>
      </div>

      <form className="tip-form" onSubmit={handleSubmit}>
        <div className="mini-item">
          <label className="mini-label" htmlFor="tipYourName">
            Name
          </label>
          <input
            id="tipYourName"
            className="text-input"
            type="text"
            placeholder="your name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="mini-item">
          <label className="mini-label" htmlFor="tipName">
            Tips Name Name
          </label>
          <input
            id="tipName"
            className="text-input"
            type="text"
            placeholder="Tips Name Name"
            value={tipName}
            onChange={(e) => setTipName(e.target.value)}
          />
        </div>

        <div className="mini-item">
          <label className="mini-label" htmlFor="tipEmail">
            Email <span aria-hidden="true">*</span>
          </label>
          <input
            id="tipEmail"
            className="text-input"
            type="email"
            placeholder="your email address"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="mini-item">
          <label className="mini-label" htmlFor="tipTelegram">
            Telegram
          </label>
          <input
            id="tipTelegram"
            className="text-input"
            type="text"
            placeholder="your telegram username"
            value={telegram}
            onChange={(e) => setTelegram(e.target.value)}
          />
        </div>

        <div className="mini-item tip-form-full">
          <label className="mini-label" htmlFor="tipBody">
            Tips <span aria-hidden="true">*</span>
          </label>
          <textarea
            id="tipBody"
            className="text-input tip-textarea"
            placeholder="your tips here"
            required
            value={tips}
            onChange={(e) => setTips(e.target.value)}
          />
        </div>

        <div className="tip-form-actions">
          <button type="submit" className="cta-button" disabled={!canSubmit}>
            <span>{loading ? '…' : '+'}</span> {loading ? 'Submitting…' : 'Submit'}
          </button>
          {status && <p className="secondary-text">{status}</p>}
        </div>
      </form>
    </section>
  );
}

export default TipsPage;


